import React, { useMemo } from 'react';

import { parseLink, validateLink } from '@aline/common';
import { Box, IconButton } from '@mui/material';
import { flip, offset } from '@platejs/floating';
import {
  FloatingLinkUrlInput,
  LinkPlugin,
  useFloatingLinkInsert,
  useFloatingLinkInsertState,
  useLinkToolbarButton,
  useLinkToolbarButtonState,
} from '@platejs/link/react';
import clsx from 'clsx';
import { Link } from 'lucide-react';
import type { TLinkElement } from 'platejs';
import type { PlateElementProps } from 'platejs/react';
import {
  PlateElement,
  useEditorPlugin,
  useEditorRef,
  useFormInputProps,
  useReadOnly,
  useSelected,
} from 'platejs/react';
import { AlineTooltip } from 'src/components';
import { useDispatchNotification } from 'src/helpers/snackbars';
import { openLink } from 'src/helpers/url';
import { makeStyles } from 'tss-react/mui';

import { ChipRemoveIcon, plateDataIds } from '../../ChatEditor';
import { focusEditor } from '../utils/focus';
import {
  getChipRemoveButtonStyles,
  getEditorChipStyles,
  getFloatingToolbarStyles,
  getToolbarButtonStyles,
} from '../utils/styles';

const useLinkStyles = makeStyles()(theme => ({
  floatingContainer: {
    ...getFloatingToolbarStyles(theme),
  },
  floatingIcon: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1),
  },
  floatingInput: {
    backgroundColor: 'transparent',
    border: 'none',
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    outline: 'none',
    padding: theme.spacing(0.5, 1.5),
    width: '100%',
  },
  floatingInputWrapper: {
    alignItems: 'center',
    display: 'flex',
  },
  linkChip: {
    ...getEditorChipStyles(theme),
  },
  linkChipSelected: {
    borderColor: theme.palette.primary.main,
  },
  linkIcon: {
    alignItems: 'center',
    display: 'flex',
    lineHeight: 0,
  },
  linkUrl: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Shared remove button and toolbar button styles
  ...getChipRemoveButtonStyles(),
  ...getToolbarButtonStyles(theme),
}));

/** Link element component - renders links as chips */
export const LinkElement = React.memo(function LinkElement(
  props: PlateElementProps<TLinkElement>
): JSX.Element {
  const { classes } = useLinkStyles();
  const readOnly = useReadOnly();
  const selected = useSelected();
  const { editor } = useEditorPlugin(LinkPlugin);
  const linkUrl = props.element.url || '';

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const path = editor.api.findPath(props.element);
    if (path) editor.tf.removeNodes({ at: path });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Sanitize at render time to prevent XSS from clipboard attacks.
    const parsed = parseLink(linkUrl);
    if (parsed.type !== 'error') openLink(parsed.href);
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [plateDataIds.link]: true,
        [plateDataIds.url]: linkUrl,
      }}
    >
      <AlineTooltip arrow placement="top" title={linkUrl}>
        <Box
          className={clsx(classes.linkChip, selected && classes.linkChipSelected)}
          component="span"
          onClick={handleClick}
        >
          {/* Hidden children for Slate's cursor positioning */}
          <span style={{ display: 'none' }}>{props.children}</span>
          {/* Link icon */}
          <Box className={classes.linkIcon} component="span">
            <Link size={16} />
          </Box>
          {/* Display the URL from the element */}
          <span className={classes.linkUrl}>{linkUrl}</span>
          {!readOnly && (
            <span className={classes.chipRemoveButton} onClick={handleRemove}>
              <ChipRemoveIcon />
            </span>
          )}
        </Box>
      </AlineTooltip>
    </PlateElement>
  );
});

/** Floating toolbar for link insertion */
export const LinkFloatingToolbar = React.memo(function LinkFloatingToolbar(): JSX.Element | null {
  const editor = useEditorRef();
  const { classes } = useLinkStyles();
  const { api } = useEditorPlugin(LinkPlugin);
  const dispatchInvalidLinkNotification = useDispatchNotification({
    message: 'Please enter a valid URL (e.g., https://example.com)',
    variant: 'error',
  });
  const floatingOptions = useMemo(
    () => ({
      middleware: [
        offset(8),
        flip({
          fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
          padding: 12,
        }),
      ],
      placement: 'bottom-start' as const,
    }),
    []
  );
  const insertState = useFloatingLinkInsertState({
    floatingOptions,
  });
  const { hidden, props: insertProps, ref: insertRef } = useFloatingLinkInsert(insertState);
  // Extract and omit style from props to handle csstype version mismatch with platejs
  const { style: insertStyle, ...restInsertProps } = insertProps;
  const inputProps = useFormInputProps({
    preventDefaultOnEnterKeydown: true,
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const url = e.currentTarget.value.trim();
      if (!validateLink(url)) {
        dispatchInvalidLinkNotification();
        return;
      }

      // Delete selected text if any.
      editor.tf.delete();
      editor.tf.insertNodes([
        {
          children: [{ text: '' }],
          type: 'a',
          url,
        } satisfies TLinkElement,
      ]);
      e.currentTarget.value = '';
      api.floatingLink.hide();
      focusEditor(editor, 'end');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.value = '';
      api.floatingLink.hide();
      focusEditor(editor);
    }
  };

  if (hidden) return null;

  return (
    <div
      ref={insertRef}
      className={classes.floatingContainer}
      data-plate-focus="true"
      // @ts-expect-error csstype version mismatch between platejs and @types/react - safe at runtime
      style={insertStyle}
      {...restInsertProps}
    >
      <div className={classes.floatingInputWrapper} {...inputProps}>
        <div className={classes.floatingIcon}>
          <Link size={16} />
        </div>
        <FloatingLinkUrlInput
          className={classes.floatingInput}
          data-plate-focus="true"
          placeholder="Type URL..."
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
});

/** Link toolbar button */
export function LinkToolbarButton({
  afterClick,
  children,
  disabled,
  tooltip,
}: {
  children: React.ReactNode;
  tooltip: string;
  afterClick?: () => void;
  disabled?: boolean;
}): JSX.Element {
  const editor = useEditorRef();
  const { classes } = useLinkStyles();
  const state = useLinkToolbarButtonState();
  const { api } = useEditorPlugin(LinkPlugin);
  const { isOpen } = useFloatingLinkInsertState();
  const { props: buttonProps } = useLinkToolbarButton(state);

  const stopEvent = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    stopEvent(e);

    if (isOpen) {
      api.floatingLink.hide();
      focusEditor(editor);
      afterClick?.();
      return;
    }

    buttonProps.onClick?.();
    afterClick?.();
  };

  return (
    <AlineTooltip arrow placement="top" title={disabled ? '' : tooltip}>
      <IconButton
        className={clsx(
          classes.toolbarButton,
          isOpen ? classes.toolbarButtonPressed : classes.toolbarButtonDefault
        )}
        data-plate-focus="true"
        disabled={disabled}
        size="small"
        onClick={handleClick}
        // onMouseDown needed to prevent PlateJS from hijacking the process...
        onMouseDown={stopEvent}
      >
        {children}
      </IconButton>
    </AlineTooltip>
  );
}

/** Configured LinkPlugin kit with element and floating toolbar */
export const LinkKit = [
  LinkPlugin.configure({
    node: {
      isVoid: true, // Treat links as void elements (non-editable, deletable as a unit)
    },
    options: {
      allowedSchemes: ['http', 'https', 'mailto', 'tel'],
      isUrl: validateLink,
      keepSelectedTextOnPaste: true,
    },
    render: {
      afterEditable: () => <LinkFloatingToolbar />,
      node: LinkElement,
    },
  }),
];
